// The purpose of this object is to run all the required javascripts against given DOM content, that
// would be consistent accross the application. It registeres two types of hooks, functional - that
// are to be executed once per content (e.g create selectize elements, initialize other libraries)
// and visuals that are to be executed every time new content is displayed (e.g. resize elements
// according to other element sizes).
//
// To register new hook, one must call Loader.functional(fn(content, resolve, reject)) or similar
// Loader.visual function. fn is to perform some action that calls resolve or reject when completed
// (similarly to regular promises).
//
// IMPORTANT: Hooks MUST use the `content` parameter for DOM queries, NOT `document`.
// - Correct:   content.querySelector('.my-element')
// - Incorrect: document.querySelector('.my-element')
//
// Event delegation at document level is acceptable (listening, not querying):
// - Acceptable: document.addEventListener('click', ...)
// - Acceptable: $(document).on('click', ...)
//
// The loader automatically detects document query usage (querySelector, querySelectorAll, getElementById)
// and emits deprecation warnings in non-production environments with stack traces. These warnings can
// be converted to errors in the future to enforce proper scoping.
//
// Edge cases that require cross-scope queries should be marked with:
// // TODO-LOADER-SCOPE: [Brief description of why document query is needed and what pattern should replace it]
//
// When adding dynamic content to the page, one need to run loader against it. The main goal is to
// ensure that all javascript has run before the content is displayed.
//
//    new Loader(myNewContent).all().then -> myNewContent.display()
//
// Note that visual hooks might require some extra setup in which it might be usefull to run those
// hooks separatly, e.g equalizer only works with element that are visible to browser (no parent
// being display: none):
//
//   loader = new Loader(myNewContent)
//   loader.functional()
//     .then (content) ->
//       content.style.setProperty('height', 0)
//       content.style.setProperty('display', 'block')
//       loader.visual()
//     .then (content) ->
//       slideDown(content)

import { createLogger, nodeToString } from "@/javascript/utils/logging"

export default class Loader {
  static functional (fn) {
    this.hooks.functional.push(fn)
  }

  static visual (fn) {
    this.hooks.visual.push(fn)
  }

  static promises = []

  static hooks = {
    functional: [],
    visual: []
  }

  constructor (content, loadingClass = 'loading') {
    this.logger = createLogger('Loader')
    this.content = content
    this.loadingClass = loadingClass
  }

  getHooks (modes) {
    const result = []
    Object.entries(this.constructor.hooks).forEach((entry) => {
      const [key, hooks] = entry
      if(modes.includes(key)) {
        result.push(...hooks)
      }
    })
    return result
  }

  start (opts = {}) {
    this.running = true
    if(this.loadingClass) {
      this.content.classList.add(this.loadingClass)
    }
    const hooks = this.getHooks(opts.modes)
    this.logger.debug(`Starting loader for ${nodeToString(this.content)} with modes: ${opts.modes.join(', ')}, hooks: ${hooks.length}`)

    // Store original document query methods before any hooks run
    const originalMethods = {
      querySelector: document.querySelector,
      querySelectorAll: document.querySelectorAll,
      getElementById: document.getElementById
    }

    // Wrap document query methods with deprecation warnings in non-production
    if (process.env.NODE_ENV !== 'production') {
      document.querySelector = function(...args) {
        console.warn(
          'DEPRECATED: Loader hook accessing document.querySelector instead of content parameter.',
          '\nThis will become an error in the future.',
          '\nCalled from:',
          new Error().stack
        )
        return originalMethods.querySelector.apply(document, args)
      }

      document.querySelectorAll = function(...args) {
        console.warn(
          'DEPRECATED: Loader hook accessing document.querySelectorAll instead of content parameter.',
          '\nThis will become an error in the future.',
          '\nCalled from:',
          new Error().stack
        )
        return originalMethods.querySelectorAll.apply(document, args)
      }

      document.getElementById = function(...args) {
        console.warn(
          'DEPRECATED: Loader hook accessing document.getElementById instead of content parameter.',
          '\nThis will become an error in the future.',
          '\nCalled from:',
          new Error().stack
        )
        return originalMethods.getElementById.apply(document, args)
      }
    }

    const restoreMethods = () => {
      if (process.env.NODE_ENV !== 'production') {
        document.querySelector = originalMethods.querySelector
        document.querySelectorAll = originalMethods.querySelectorAll
        document.getElementById = originalMethods.getElementById
      }
    }

    return Promise.all(
      hooks.map((hook) => {
        return ((hook) => {
          return Promise.race([
            new Promise((resolve, reject) => {
              hook(this.content, resolve, reject)
            }),
            new Promise((resolve) => {
              const func = () => {
                if(this.running) {
                  this.logger.error(`Hook timed out after ${opts.timeout || 2000}ms: ${hook.toString().substring(0, 100)}...`)
                }
                resolve()
              }
              return setTimeout(func, opts.timeout || 2000)
            })
          ])
        })(hook)
      })
    )
    .catch((e) => this.logger.error(e.message || e))
    .then(() => {
      restoreMethods()
      this.running = false
      this.logger.debug(`Completed loader for ${nodeToString(this.content)} with modes: ${opts.modes.join(', ')}`)
    })
    .then(() => {
      if(this.loadingClass) {
        this.content.classList.remove(this.loadingClass)
      }
    })
    .then(() => this.content)
  }

  all () {
    return this.start({modes: ['functional', 'visual']})
  }

  functional () {
    return this.start({modes: ['functional']})
  }
  visual () {
    return this.start({modes: ['visual']})
  }
}

document.addEventListener("turbo:before-stream-render", ((event) => {
  const fallbackToDefaultActions = event.detail.render

  event.detail.render = async function (streamElement) {
    fallbackToDefaultActions(streamElement)
    const target = document.getElementById(streamElement.target)
    if(target) {
      await new Loader(target).all()
    }
  }
}))

/* This is split into two methods because there are loading hooks that access
 * document, which means the new content needs to be present in the DOM.
 *
 * If this is changed then this code can be done in a single event listener as:
 *
 * document.addEventListener("turbo:before-frame-render", ((event) => {
 *   event.preventDefault();
 *
 *   new Loader(event.detail.newFrame).all()
 *     .then(() => {
 *       event.detail.resume();
 *     });
 * }))
 */
document.addEventListener("turbo:before-frame-render", ((event) => {
  event.detail.newFrame.childNodes.forEach(function(child) {
    child.classList.add('loading')
  })
}))

document.addEventListener("turbo:frame-render", ((event) => {
  event.target.childNodes.forEach(function(child) {
    new Loader(child).all()
  })
}))

document.addEventListener('resized', ((event) => {
  new Loader(event.target).visual()
}))
